import { useMemo, type ReactNode } from 'react';
import { AccountSwitcher } from '@/components/admin/account-switcher';
import { DashboardTrousse } from '@/features/trousse/dashboard-trousse';
import { Link } from '@tanstack/react-router';
import {
  ArrowRight,
  BookOpen,
  CalendarClock,
  CalendarPlus,
  Check,
  ChevronRight,
  Clock,
  Inbox,
  MessageCircle,
  Plus,
  Swords,
  UserPlus,
  Users,
  Video,
} from 'lucide-react';
import { Button, MathText, cn } from '@jeraisonne/ui';
import { CountUp } from '@/components/count-up';
import { RENDEZ_VOUS_KIND_LABELS } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useNow } from '@/hooks/use-now';
import { AvatarCircle } from '@/components/avatar-circle';
import { useTutorDashboardStats, useTutorStudents } from '@/lib/tutor/queries';
import { useTutorBookingRules, useTutorBookingSlots } from '@/lib/tutor/booking';
import { useMyArchivedRendezVousIds } from '@/lib/rendezvous-archive';
import { useJoinVisioRoom } from '@/lib/visio/handoff';
import { useTutorSubmissions } from '@/lib/exercise-submissions/api';
import { useDiscussions } from '@/lib/discussions/queries';
import { countUnreadDiscussions, hasUnreadDiscussion } from '@/lib/discussions/discussion-unread';
import {
  agoLabel,
  buildAgenda,
  firstName,
  recentActivityRows,
  useTutorRecentAttempts,
  weekStats,
  type ActivityRow,
  onboardingSteps,
  studentsToFollowUp,
  summarySentence,
  timeLabel,
  useTutorOverdueAssignments,
  type AgendaItem,
  type FollowUp,
} from '@/lib/tutor/dashboard-overview';

function greeting(): string {
  const h = new Date().getHours();
  if (h < 18) return 'Bonjour';
  return 'Bonsoir';
}

const FOLLOW_UP_SHOWN = 3;

/**
 * Accueil prof : les chiffres de la semaine (cliquables), les actions rapides,
 * puis ce qui attend une action (copies, messages, demandes), l'agenda du jour
 * et du lendemain, les élèves à relancer et l'activité récente de ses élèves.
 * Les chiffres de « catalogue » (élèves, classes, exercices) passent en bas.
 */
export function TutorDashboardPage() {
  const { user, profile } = useAuth();
  const tutorId = user?.id ?? '';
  const now = useNow(60_000);

  const { data: students = [], isLoading: studentsLoading } = useTutorStudents(tutorId);
  const { data: slots = [], isLoading: slotsLoading } = useTutorBookingSlots(tutorId);
  const { data: archivedIds } = useMyArchivedRendezVousIds(tutorId);
  const { data: submissions = [] } = useTutorSubmissions(tutorId);
  const { data: discussions = [] } = useDiscussions();
  const { data: overdue = [] } = useTutorOverdueAssignments(tutorId);
  const { data: rules = [], isLoading: rulesLoading } = useTutorBookingRules(tutorId);
  const { data: stats } = useTutorDashboardStats(tutorId);
  const activeIds = useMemo(
    () => students.filter((s) => s.status === 'active').map((s) => s.student_id),
    [students],
  );
  const { data: recentAttempts = [] } = useTutorRecentAttempts(tutorId, activeIds);

  const agenda = useMemo(() => buildAgenda(slots, now, archivedIds), [slots, now, archivedIds]);
  const followUps = useMemo(
    () => studentsToFollowUp(students, overdue, now),
    [students, overdue, now],
  );
  const requests = students.filter((s) => s.status === 'pending' && s.initiated_by === 'student');
  const todo = {
    submissions: submissions.length,
    unreadDiscussions: countUnreadDiscussions(discussions, 'tutor'),
    requests: requests.length,
  };
  const todayCount = agenda.days[0]?.label === "Aujourd'hui" ? agenda.days[0].items.length : 0;

  const activeStudents = activeIds.length;
  const week = useMemo(
    () => weekStats(students, recentAttempts, now),
    [students, recentAttempts, now],
  );
  const activity = useMemo(
    () => recentActivityRows(recentAttempts, students),
    [recentAttempts, students],
  );
  const todoTotal = todo.submissions + todo.unreadDiscussions + todo.requests;
  const steps = onboardingSteps({
    hasRules: rules.length > 0,
    hasStudents: activeStudents > 0,
    hasClasses: (stats?.active_classes ?? 0) > 0,
  });
  const loading = studentsLoading || slotsLoading || rulesLoading;
  const isNew = !loading && steps.some((s) => !s.done) && activeStudents === 0;
  const name = firstName(profile?.display_name);

  return (
    <div className="pb-safe mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      <header className="mb-6 flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-accent-500 text-xs font-semibold uppercase tracking-[.14em]">
            {new Date(now).toLocaleDateString('fr-FR', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
            })}
          </p>
          <h1 className="font-display text-ink-900 mt-1 text-2xl font-extrabold tracking-tight md:text-3xl">
            {greeting()}
            {name ? `, ${name}` : ''}
          </h1>
          {!loading && !isNew && (
            <p className="text-ink-600 mt-1 font-sans text-sm" data-testid="tutor-summary">
              {summarySentence(todayCount, todo, followUps.length)}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-start gap-2 self-start">
          <AccountSwitcher />
          <DashboardTrousse />
        </div>
      </header>

      {loading ? (
        <div className="space-y-4" aria-busy>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-ink-100 h-24 animate-pulse rounded-2xl" />
            ))}
          </div>
          <div className="bg-ink-100 h-40 animate-pulse rounded-2xl" />
        </div>
      ) : isNew ? (
        <div className="space-y-6">
          <OnboardingCard steps={steps} />
          <QuickActions />
        </div>
      ) : (
        <div className="space-y-6">
          <KpiGrid today={todayCount} todo={todoTotal} week={week} />
          <QuickActions />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start">
            <div className="space-y-6">
              <TodoSection
                todo={todo}
                submissionNames={submissions.map((s) => s.student_name)}
                discussionNames={discussions
                  .filter((d) => hasUnreadDiscussion(d, 'tutor'))
                  .map((d) => d.studentName ?? 'Élève')}
              />
              <AgendaSection days={agenda.days} later={agenda.later} />
            </div>
            <div className="space-y-6">
              <FollowUpSection followUps={followUps} />
              <RecentActivitySection rows={activity} now={now} />
              {steps.some((s) => !s.done) && <OnboardingCard steps={steps} compact />}
              <CatalogLine
                students={activeStudents}
                classes={stats?.active_classes ?? 0}
                exercises={stats?.total_exercises ?? 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Chiffres ───────────────────────────────────────────────────────────────

type KpiTo = '/tutor/rendez-vous' | '/tutor/contacts' | '/tutor/submissions';

function KpiTile({
  icon,
  tone,
  value,
  label,
  detail,
  to,
  alert = false,
}: {
  icon: string;
  tone: string;
  value: ReactNode;
  label: string;
  detail?: string;
  to: KpiTo;
  alert?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'border-ink-100 ocean:bg-white/5 flex h-full min-h-[2.75rem] min-w-0 flex-col items-center gap-1 rounded-2xl border bg-white px-1 py-2.5 text-center active:scale-[0.98] sm:items-start sm:gap-2 sm:p-4 sm:text-left',
        alert && 'border-accent-300 ocean:border-accent-400/50',
      )}
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-lg text-sm sm:h-9 sm:w-9 sm:rounded-xl sm:text-lg ${tone}`}
        aria-hidden
      >
        {icon}
      </span>
      <span className="font-display text-ink-900 text-lg font-extrabold tabular-nums leading-none sm:text-3xl">
        {value}
      </span>
      <span className="w-full min-w-0">
        <span className="text-ink-700 block font-sans text-xs font-semibold leading-tight sm:text-sm">
          {label}
        </span>
        {detail && (
          <span className="text-ink-500 sr-only font-sans text-xs leading-tight sm:not-sr-only sm:block">
            {detail}
          </span>
        )}
      </span>
    </Link>
  );
}

function KpiGrid({
  today,
  todo,
  week,
}: {
  today: number;
  todo: number;
  week: ReturnType<typeof weekStats>;
}) {
  return (
    <section aria-label="Mes chiffres" className="grid grid-cols-4 gap-1.5 sm:gap-3">
      <KpiTile
        to="/tutor/rendez-vous"
        icon="📅"
        tone="bg-[#dbeafe] ocean:bg-[#12305e]"
        value={<CountUp value={today} />}
        label="Aujourd’hui"
        detail="rendez-vous"
      />
      <KpiTile
        to="/tutor/submissions"
        icon="📥"
        tone="bg-[#fef3c7] ocean:bg-[#433410]"
        value={<CountUp value={todo} />}
        label="À traiter"
        detail={todo === 0 ? 'tout est à jour' : 'copies, messages, demandes'}
        alert={todo > 0}
      />
      <KpiTile
        to="/tutor/contacts"
        icon="👥"
        tone="bg-[#ede9fe] ocean:bg-[#33215e]"
        value={
          <>
            <CountUp value={week.activeThisWeek} />
            <span className="text-ink-400 text-xs font-bold sm:text-lg">/{week.students}</span>
          </>
        }
        label="Élèves actifs"
        detail="sur 7 jours"
      />
      <KpiTile
        to="/tutor/contacts"
        icon="🎯"
        tone="bg-[#dcfce7] ocean:bg-[#0f3b2c]"
        value={
          week.avgScore == null ? '—' : <CountUp value={week.avgScore} format={(n) => `${n}%`} />
        }
        label="Réussite"
        detail={`${week.completed} exercice${week.completed > 1 ? 's' : ''} sur 7 jours`}
      />
    </section>
  );
}

// ─── Agenda ─────────────────────────────────────────────────────────────────

function SectionHeader({
  title,
  to,
  linkLabel,
}: {
  title: string;
  to?: string;
  linkLabel?: string;
}) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className="font-display text-ink-500 text-xs font-bold uppercase tracking-wider">
        {title}
      </h2>
      {to && linkLabel && (
        <Link
          to={to}
          className="text-primary-600 -my-2 inline-flex min-h-[2.75rem] items-center gap-1 font-sans text-sm font-semibold"
        >
          {linkLabel}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      )}
    </div>
  );
}

function AgendaSection({
  days,
  later,
}: {
  days: ReturnType<typeof buildAgenda>['days'];
  later: number;
}) {
  return (
    <section aria-label="Agenda">
      <SectionHeader title="Agenda" to="/tutor/rendez-vous" linkLabel="Tout l’agenda" />
      <div className="border-ink-100 ocean:bg-white/5 rounded-2xl border bg-white p-4">
        {days.length === 0 ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-ink-700 flex items-center gap-2 font-sans text-sm">
              <CalendarClock className="text-ink-400 h-5 w-5 shrink-0" aria-hidden />
              Aucun rendez-vous aujourd’hui ni demain.
            </p>
            <Link
              to="/tutor/rendez-vous/new"
              className="border-ink-200 text-ink-800 hover:border-primary-400 inline-flex min-h-[2.75rem] w-full items-center justify-center gap-2 rounded-xl border px-4 font-sans text-sm font-semibold active:scale-[0.98] sm:w-auto"
            >
              <Video className="h-4 w-4" aria-hidden />
              Programmer un rendez-vous
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {days.map((day) => (
              <div key={day.key}>
                <p className="font-display text-ink-900 mb-2 text-sm font-extrabold first-letter:uppercase">
                  {day.label}
                </p>
                <ul className="space-y-2">
                  {day.items.map((item) => (
                    <AgendaRow key={item.reservationId} item={item} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
        {later > 0 && (
          <p className="text-ink-500 border-ink-100 mt-3 border-t pt-3 font-sans text-sm">
            + {later} autre{later > 1 ? 's' : ''} rendez-vous plus tard
          </p>
        )}
      </div>
    </section>
  );
}

function AgendaRow({ item }: { item: AgendaItem }) {
  const join = useJoinVisioRoom();
  const pending = item.status === 'pending_payment';
  const body = (
    <>
      <span className="font-display text-ink-900 w-12 shrink-0 text-base font-extrabold tabular-nums">
        {timeLabel(item.startsAt)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink-900 block truncate font-sans text-sm font-semibold">
          {item.name}
        </span>
        <span className="text-ink-500 block font-sans text-xs">
          {RENDEZ_VOUS_KIND_LABELS[item.kind]}
          {item.priceCents > 0 && ` · ${(item.priceCents / 100).toLocaleString('fr-FR')} €`}
          {item.kind === 'cours' && !pending && item.priceCents > 0 && ' · payé'}
        </span>
        {pending && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-sans text-xs font-semibold text-amber-700">
            <Clock className="h-3 w-3" aria-hidden />
            En attente de paiement
          </span>
        )}
      </span>
    </>
  );

  if (item.joinable) {
    return (
      <li className="bg-primary-50/70 ocean:bg-primary-500/10 flex flex-col gap-3 rounded-xl p-3 sm:flex-row sm:items-center">
        <div className="flex min-w-0 flex-1 items-center gap-3">{body}</div>
        <Button
          onClick={() => join.mutate(item.reservationId)}
          loading={join.isPending}
          className="min-h-[2.75rem] w-full active:scale-[0.98] sm:w-auto"
        >
          <Video className="size-4" aria-hidden />
          Rejoindre la salle
        </Button>
      </li>
    );
  }
  return (
    <li>
      <Link
        to={pending ? '/tutor/rendez-vous' : '/rendez-vous/$reservationId'}
        params={pending ? undefined : { reservationId: item.reservationId }}
        className="hover:bg-ink-50 flex min-h-[2.75rem] items-center gap-3 rounded-xl p-2 active:scale-[0.98]"
      >
        {body}
        <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" aria-hidden />
      </Link>
    </li>
  );
}

// ─── À traiter ──────────────────────────────────────────────────────────────

function namesPreview(names: string[]): string {
  const firsts = [...new Set(names.map((n) => n.split(' ')[0]))];
  if (firsts.length <= 2) return firsts.join(' et ');
  return `${firsts.slice(0, 2).join(', ')} et ${firsts.length - 2} autre${firsts.length > 3 ? 's' : ''}`;
}

function TodoSection({
  todo,
  submissionNames,
  discussionNames,
}: {
  todo: { submissions: number; unreadDiscussions: number; requests: number };
  submissionNames: string[];
  discussionNames: string[];
}) {
  const rows: { key: string; icon: ReactNode; label: string; detail?: string; to: string }[] = [];
  if (todo.submissions > 0)
    rows.push({
      key: 'submissions',
      icon: <Inbox className="h-5 w-5" aria-hidden />,
      label: `${todo.submissions} exercice${todo.submissions > 1 ? 's' : ''} reçu${todo.submissions > 1 ? 's' : ''} à voir`,
      detail: `De ${namesPreview(submissionNames)}`,
      to: '/tutor/submissions',
    });
  if (todo.unreadDiscussions > 0)
    rows.push({
      key: 'discussions',
      icon: <MessageCircle className="h-5 w-5" aria-hidden />,
      label: `${todo.unreadDiscussions} discussion${todo.unreadDiscussions > 1 ? 's' : ''} non lue${todo.unreadDiscussions > 1 ? 's' : ''}`,
      detail: discussionNames.length ? `Avec ${namesPreview(discussionNames)}` : undefined,
      to: '/tutor/discussions',
    });
  if (todo.requests > 0)
    rows.push({
      key: 'requests',
      icon: <UserPlus className="h-5 w-5" aria-hidden />,
      label: `${todo.requests} demande${todo.requests > 1 ? 's' : ''} d’élève à accepter`,
      to: '/tutor/contacts',
    });

  if (rows.length === 0)
    return (
      <section aria-label="À traiter">
        <SectionHeader title="À traiter" />
        <p className="border-ink-100 ocean:bg-white/5 text-ink-700 flex items-center gap-2 rounded-2xl border bg-white p-4 font-sans text-sm">
          <Check className="text-xp-500 h-5 w-5 shrink-0" aria-hidden />
          Rien à traiter : aucun exercice reçu, message ou demande en attente.
        </p>
      </section>
    );
  return (
    <section aria-label="À traiter">
      <SectionHeader title="À traiter" />
      <ul className="border-ink-100 ocean:bg-white/5 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
        {rows.map((row) => (
          <li key={row.key}>
            <Link
              to={row.to}
              className="hover:bg-ink-50 flex min-h-[3.25rem] items-center gap-3 px-4 py-3 active:scale-[0.99]"
            >
              <span className="bg-accent-50 text-accent-600 ocean:bg-accent-400/15 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                {row.icon}
              </span>
              <span className="min-w-0 flex-1">
                <span className="text-ink-900 block font-sans text-sm font-semibold">
                  {row.label}
                </span>
                {row.detail && (
                  <span className="text-ink-500 block font-sans text-xs">{row.detail}</span>
                )}
              </span>
              <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── Élèves à relancer ──────────────────────────────────────────────────────

function FollowUpSection({ followUps }: { followUps: FollowUp[] }) {
  if (followUps.length === 0) {
    return (
      <section aria-label="Élèves à relancer">
        <SectionHeader title="Élèves à relancer" to="/tutor/contacts" linkLabel="Mes élèves" />
        <p className="border-ink-100 ocean:bg-white/5 text-ink-700 flex items-center gap-2 rounded-2xl border bg-white p-4 font-sans text-sm">
          <Check className="text-xp-500 h-5 w-5 shrink-0" aria-hidden />
          Tous tes élèves ont travaillé cette semaine et aucun devoir n’est en retard.
        </p>
      </section>
    );
  }
  const shown = followUps.slice(0, FOLLOW_UP_SHOWN);
  const more = followUps.length - shown.length;
  return (
    <section aria-label="Élèves à relancer">
      <SectionHeader
        title={`Élèves à relancer (${followUps.length})`}
        to="/tutor/contacts"
        linkLabel="Mes élèves"
      />
      <ul className="border-ink-100 ocean:bg-white/5 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
        {shown.map((f) => (
          <li key={f.studentId}>
            <Link
              to="/tutor/students/$studentId"
              params={{ studentId: f.studentId }}
              className="hover:bg-ink-50 flex min-h-[3.25rem] items-center gap-3 px-4 py-3 active:scale-[0.99]"
            >
              <AvatarCircle name={f.name} size="sm" />
              <span className="min-w-0 flex-1">
                <span className="text-ink-900 block truncate font-sans text-sm font-semibold">
                  {f.name}
                </span>
                <span
                  className={cn(
                    'block font-sans text-xs',
                    f.overdue > 0 ? 'text-error-600' : 'text-amber-700',
                  )}
                >
                  {f.reason}
                </span>
              </span>
              <ChevronRight className="text-ink-300 h-4 w-4 shrink-0" aria-hidden />
            </Link>
          </li>
        ))}
      </ul>
      {more > 0 && (
        <p className="text-ink-500 mt-2 px-1 font-sans text-sm">
          + {more} autre{more > 1 ? 's' : ''} élève{more > 1 ? 's' : ''} à relancer
        </p>
      )}
    </section>
  );
}

// ─── Démarrage (nouveau prof) ───────────────────────────────────────────────

const STEP_COPY = {
  students: {
    title: 'Invite tes élèves',
    text: 'Un lien ou un QR code suffit : ils te rejoignent en un geste.',
    to: '/tutor/contacts',
    cta: 'Inviter',
    icon: UserPlus,
  },
  slots: {
    title: 'Ouvre tes premiers créneaux',
    text: 'Cours payants ou sessions d’étude gratuites, chaque semaine.',
    to: '/tutor/rendez-vous/creneaux',
    cta: 'Ouvrir',
    icon: CalendarPlus,
  },
  class: {
    title: 'Crée ta première classe',
    text: 'Un code d’invitation par classe, des devoirs pour tout le groupe.',
    to: '/tutor/classes/new',
    cta: 'Créer',
    icon: Users,
  },
} as const;

function OnboardingCard({
  steps,
  compact = false,
}: {
  steps: ReturnType<typeof onboardingSteps>;
  compact?: boolean;
}) {
  const done = steps.filter((s) => s.done).length;
  return (
    <section
      aria-label="Bien démarrer"
      className="border-primary-200 bg-primary-50/60 ocean:bg-white/5 space-y-4 rounded-2xl border p-5"
    >
      <div>
        <h2 className="font-display text-ink-900 text-lg font-extrabold">
          {compact ? 'Termine ton installation' : 'Bienvenue ! 3 étapes pour bien démarrer'}
        </h2>
        <p className="text-ink-600 mt-1 font-sans text-sm">
          {done} / {steps.length} fait{done > 1 ? 'es' : ''}
        </p>
      </div>
      <ol className="space-y-2">
        {steps.map((step) => {
          const copy = STEP_COPY[step.id];
          const Icon = copy.icon;
          return (
            <li key={step.id}>
              <Link
                to={copy.to}
                className={cn(
                  'ocean:bg-white/5 flex min-h-[3.25rem] items-center gap-3 rounded-xl bg-white p-3 active:scale-[0.98]',
                  step.done && 'opacity-70',
                )}
              >
                <span
                  className={cn(
                    'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
                    step.done ? 'bg-xp-50 text-xp-600' : 'bg-primary-100 text-primary-700',
                  )}
                >
                  {step.done ? (
                    <Check className="h-5 w-5" aria-hidden />
                  ) : (
                    <Icon className="h-5 w-5" aria-hidden />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'text-ink-900 block font-sans text-sm font-semibold',
                      step.done && 'line-through',
                    )}
                  >
                    {copy.title}
                  </span>
                  {!step.done && (
                    <span className="text-ink-500 block font-sans text-xs">{copy.text}</span>
                  )}
                </span>
                {!step.done && (
                  <span className="text-primary-600 inline-flex shrink-0 items-center gap-1 font-sans text-sm font-semibold">
                    {copy.cta}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                )}
              </Link>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

// ─── Secondaire ─────────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { to: '/tutor/rendez-vous/new', label: 'Rendez-vous', icon: Video, tone: 'bg-primary-500' },
  { to: '/tutor/contacts', label: 'Inviter un élève', icon: UserPlus, tone: 'bg-[#7c3aed]' },
  { to: '/live/prive/nouveau', label: 'Défi live', icon: Swords, tone: 'bg-[#e11d48]' },
  { to: '/tutor/classes/new', label: 'Nouvelle classe', icon: Plus, tone: 'bg-[#059669]' },
  { to: '/tutor/cards', label: 'SKILLCARDS', icon: BookOpen, tone: 'bg-[#d97706]' },
] as const;

function QuickActions() {
  return (
    <section aria-label="Actions rapides">
      <SectionHeader title="Actions rapides" />
      <ul className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:grid sm:grid-cols-5 sm:overflow-visible sm:px-0">
        {QUICK_ACTIONS.map((a) => {
          const Icon = a.icon;
          return (
            <li key={a.to} className="w-[5.75rem] shrink-0 sm:w-auto">
              <Link
                to={a.to}
                className="border-ink-100 ocean:bg-white/5 hover:border-primary-300 flex h-full min-h-[2.75rem] flex-col items-center gap-2 rounded-2xl border bg-white px-2 py-3 text-center active:scale-[0.97]"
              >
                <span
                  className={`flex h-11 w-11 items-center justify-center rounded-full text-white ${a.tone}`}
                >
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="text-ink-800 font-sans text-xs font-semibold leading-tight">
                  {a.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ─── Activité récente ───────────────────────────────────────────────────────

function scoreTone(score: number | null): string {
  if (score == null) return 'bg-ink-100 text-ink-600';
  if (score >= 80) return 'bg-xp-50 text-xp-700 ocean:bg-xp-500/15';
  if (score >= 50) return 'bg-amber-50 text-amber-700 ocean:bg-amber-400/15';
  return 'bg-error-50 text-error-600 ocean:bg-error-500/15';
}

function RecentActivitySection({ rows, now }: { rows: ActivityRow[]; now: number }) {
  return (
    <section aria-label="Activité de mes élèves">
      <SectionHeader title="Activité de mes élèves" to="/tutor/news" linkLabel="Le Feed" />
      {rows.length === 0 ? (
        <p className="border-ink-100 ocean:bg-white/5 text-ink-600 rounded-2xl border bg-white p-4 font-sans text-sm">
          Aucun exercice terminé par tes élèves ces 7 derniers jours.
        </p>
      ) : (
        <ul className="border-ink-100 ocean:bg-white/5 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to="/attempts/$attemptId"
                params={{ attemptId: r.id }}
                className="hover:bg-ink-50 flex min-h-[3.25rem] items-center gap-3 px-4 py-3 active:scale-[0.99]"
              >
                <AvatarCircle name={r.name} size="sm" />
                <span className="min-w-0 flex-1">
                  <span className="text-ink-900 block truncate font-sans text-sm font-semibold">
                    {r.name}
                  </span>
                  <span className="text-ink-500 block truncate font-sans text-xs">
                    <MathText>{r.exercise_title}</MathText> · {agoLabel(r.completed_at, now)}
                  </span>
                </span>
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 font-sans text-xs font-bold tabular-nums',
                    scoreTone(r.score),
                  )}
                >
                  {r.score == null ? '—' : `${Math.round(r.score)} %`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CatalogLine({
  students,
  classes,
  exercises,
}: {
  students: number;
  classes: number;
  exercises: number;
}) {
  const parts = [
    students > 0 && `${students} élève${students > 1 ? 's' : ''}`,
    classes > 0 && `${classes} classe${classes > 1 ? 's' : ''}`,
    exercises > 0 &&
      `${exercises} exercice${exercises > 1 ? 's' : ''} créé${exercises > 1 ? 's' : ''}`,
  ].filter(Boolean);
  if (parts.length === 0) return null;
  return <p className="text-ink-500 px-1 font-sans text-sm">{parts.join(' · ')}</p>;
}
